import { Link } from 'react-router-dom';
import { useCart } from '@/context/CartContext';
import { products } from '@/mocks/products';

const featured = products.slice(0, 6);

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`text-xs ${
            star <= Math.floor(rating)
              ? 'ri-star-fill text-[#C4FF61]'
              : star - 0.5 <= rating
              ? 'ri-star-half-fill text-[#C4FF61]'
              : 'ri-star-line text-gray-300'
          }`}
        ></i>
      ))}
    </div>
  );
}

export default function FeaturedProducts() {
  const { addItem } = useCart();

  return (
    <section className="bg-[#F7F8F9] py-20 md:py-28">
      <div className="px-6 md:px-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="font-outfit text-xs uppercase tracking-[0.2em] text-gray-400 mb-2">Featured Collection</p>
            <h2 className="font-outfit font-black text-[#1A1A1A] text-4xl md:text-5xl leading-none">
              Trail-Tested<br />
              <em className="not-italic text-[#1A1A1A]/40">Equipment</em>
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <p className="text-sm font-inter text-gray-500 max-w-xs">
              Every piece rigorously tested across the most demanding terrain from Sonoran desert to Rocky Mountain alpine.
            </p>
            <Link
              to="/products"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-[#1A1A1A] text-white font-outfit font-medium text-sm px-6 py-3 rounded-full hover:bg-[#2A2A2A] transition-colors cursor-pointer whitespace-nowrap"
            >
              View All
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {featured.map((product, i) => {
            const isDark = i === 1;
            return (
              <div
                key={product.id}
                className={`rounded-2xl overflow-hidden group transition-all duration-300 hover:-translate-y-1 ${
                  isDark ? 'bg-[#2A2A2A]' : 'bg-white'
                }`}
              >
                {/* Image */}
                <Link to={`/product/${product.id}`} className="block relative overflow-hidden h-56 cursor-pointer">
                  <img
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {product.badge && (
                    <span className="absolute top-3 left-3 bg-[#C4FF61] text-[#1A1A1A] font-outfit font-semibold text-xs px-3 py-1 rounded-full">
                      {product.badge}
                    </span>
                  )}
                </Link>

                {/* Info */}
                <div className="p-5">
                  <p className={`text-xs font-outfit uppercase tracking-widest mb-1 ${isDark ? 'text-[#C4FF61]/70' : 'text-gray-400'}`}>
                    {product.category}
                  </p>
                  <Link to={`/product/${product.id}`} className="cursor-pointer">
                    <h3 className={`font-outfit font-bold text-base leading-snug mb-2 hover:opacity-70 transition-opacity ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`}>
                      {product.name}
                    </h3>
                  </Link>
                  <div className="flex items-center gap-2 mb-3">
                    <StarRating rating={product.rating} />
                    <span className={`text-xs font-inter ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                      ({product.reviewCount})
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-2">
                      <span className={`font-outfit font-black text-lg ${isDark ? 'text-white' : 'text-[#1A1A1A]'}`}>
                        ${product.price.toFixed(2)}
                      </span>
                      {product.originalPrice && (
                        <span className={`font-inter text-sm line-through ${isDark ? 'text-white/30' : 'text-gray-300'}`}>
                          ${product.originalPrice.toFixed(2)}
                        </span>
                      )}
                    </div>
                    <button
                      onClick={() => addItem({
                        id: product.id,
                        name: product.name,
                        price: product.price,
                        image: product.images[0],
                        category: product.category,
                      })}
                      className="w-9 h-9 flex items-center justify-center bg-[#C4FF61] text-[#1A1A1A] rounded-full hover:bg-[#a8e84a] transition-colors cursor-pointer"
                      aria-label={`Add ${product.name} to cart`}
                    >
                      <i className="ri-shopping-cart-line text-sm"></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
